import { memo, useState } from "react";
import { RefreshCw, UserCheck, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Spinner } from "@/shared/components/ui/spinner";
import { formatCurrency, formatDocument } from "@/shared/lib/formatters";
import { parseSefazMessage } from "@/shared/lib/sefaz-message";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import { InvoiceClientSearch } from "./InvoiceClientSearch";
import type {
  InvoiceCategory,
  InvoiceResponseDTO,
  ResendInvoiceRequestDTO,
} from "../types/invoice.types";

interface InvoiceResendDialogProps {
  open: boolean;
  loading: boolean;
  invoice: InvoiceResponseDTO | null;
  category: InvoiceCategory;
  onClose: () => void;
  onConfirm: (data?: ResendInvoiceRequestDTO) => void;
}

export const InvoiceResendDialog = memo(function InvoiceResendDialog({
  open,
  loading,
  invoice,
  category,
  onClose,
  onConfirm,
}: InvoiceResendDialogProps) {
  const [client, setClient] = useState<ClientResponseDTO | null>(null);

  const needsClient = category === "MANUAL";
  const canSubmit = !loading && (!needsClient || client !== null);
  const previousError = parseSefazMessage(invoice?.errorDescription);

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm(client ? { client: { clientId: client.id } } : undefined);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Retentar emissão</DialogTitle>
          <DialogDescription>
            A nota é reenviada com uma nova referência, o que resolve as recusas
            por RPS já utilizado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border border-amber-500/20 bg-amber-500/10 p-4">
            <div className="flex items-start gap-3">
              <RefreshCw className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-semibold">Nota fiscal #{invoice?.id}</p>
                {invoice && (
                  <p className="text-sm text-muted-foreground">
                    Valor: {formatCurrency(invoice.value)}
                  </p>
                )}
                {previousError && (
                  <p className="text-sm text-muted-foreground">
                    Recusa anterior: {previousError.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {needsClient ? (
            <div className="space-y-2">
              <Label>Tomador *</Label>
              {client ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-input p-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <UserCheck className="w-4 h-4 text-emerald-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {client.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDocument(client.document)}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setClient(null)}
                    title="Trocar tomador"
                    aria-label="Trocar tomador"
                    disabled={loading}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <InvoiceClientSearch onSelect={setClient} />
              )}
              <p className="text-xs text-muted-foreground">
                Nota avulsa exige um tomador já cadastrado — o reenvio não cria
                cliente.
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              O tomador é resolvido automaticamente a partir do vínculo da nota.
            </p>
          )}
        </div>

        <DialogFooter className="mt-6 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Voltar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="gap-2"
          >
            {loading && <Spinner className="w-4 h-4" />}
            Retentar emissão
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
